<script lang="ts" setup>
import { storeToRefs } from 'pinia';

import { useCartStore } from '~/stores/cart/useCartStore';

const cartStore = useCartStore();

const {
    totalSumm,
    cartValues,
    cart
} = storeToRefs(cartStore);
const {
    removeFromCart,
    clearCart
} = cartStore;

const emit = defineEmits(['toPayment']);

</script>

<template>
  <div class="cart-content display-flex flex-column height-full">
    <div class="cart-content-header">
      <h3 class="compact-500-accent mb-4">
        My cart
      </h3>
      <div class="display-flex cart-content-info justify-between align-items-center">
        <span class="text-400-default cart-content--gray">{{ cart.size }} item</span>
        <span class="text-400-default cart-content-info__clear cursor-pointer"
              @click="clearCart()"
        >Clear all</span>
      </div>
    </div>

    <div v-if="cartValues.active.length"
         class="cart-content-list">
      <CartItem v-for="({ displayImageURL, price, name, uuid, itemType, amount, maxAmount, isError }, index) in cartValues.active"
                :key="uuid"
                :is-error="isError"
                :max-amount="maxAmount"
                class="display-flex"
                :class="index + 1 !== cartValues.active.length && 'cart-content-list__item'"
                :displayImageURL="displayImageURL"
                :price="`${price?.amount} ${price?.symbol}`"
                :name="name"
                :amount="amount"
                :itemType="itemType"
                :uuid="uuid"
                @on-remove="removeFromCart(uuid)"
      />
    </div>
    <div v-if="cartValues.expired.length"
         class="cart-content-list">
      <h4 class="compact-400-default cart-content--gray mb-8">Listing expired</h4>
      <CartItem v-for="({ displayImageURL, name, uuid, itemType, maxAmount, isError }, index) in cartValues.expired"
                :key="uuid"
                :is-error="isError"
                :max-amount="maxAmount"
                class="display-flex"
                :class="index + 1 !== cartValues.expired.length && 'cart-content-list__item'"
                :displayImageURL="displayImageURL"
                :name="name"
                :itemType="itemType"
                :uuid="uuid"
                @on-remove="removeFromCart(uuid)"
      />
    </div>
    <div class="cart-content-footer">
      <CartTotal class="cart-content-total"
                 v-bind="totalSumm"
      />
      <button type="button"
              class="cart-content__checkout display-flex justify-center align-items-center compact-500-accent gap-8 cursor-pointer"
              @click="emit('toPayment')"
      >
        Checkout
        <IconMain icon="arrow-r-l"
                  :size="24"
        />
      </button>
    </div>
  </div>
</template>

<style lang="scss">

.cart {
  &-content {
    position: relative;
    flex-grow: 1;

    &-footer {
      margin-top: auto;
    }

    &--gray {
      color: var(--content-secondary)
    }

    &-info {
      margin-bottom: $spacing-600;
      padding-bottom: $spacing-600;
      border-bottom: 1px solid var(--border-content);

      &__clear {
        color: var(--bg-brand-mojito-primary, #92FE75);
      }
    }

    &-total {
      border-top: 1px solid var(--border-content);
    }

    &-list {
      max-height: 300px;
      margin-bottom: $spacing-300;
      padding-bottom: $spacing-300;
      overflow: hidden auto;

      @include tablet-768 {
        max-height: 100%;
      }

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        border-radius: 100px;
        background: var(--layer-floor-2);
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 100px;
        background-color: var(--content-secondary);
      }

      &__item {
        margin-bottom: $spacing-500;
      }
    }

    &__checkout {
      align-self: stretch;
      width: 100%;
      height: var(--control-500-size-single, 52px);
      transition: opacity 0.3s ease;
      border-radius: var(--control-500-radius, 2px);
      background: var(--bg-brand-mojito-primary);

      &:hover {
        opacity: 0.8;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
